import { Box, CloseIcon, Spinner, ViewIcon } from "@src/components/elements";
import { allowedDocTypes } from "@src/components/modules/Upload/UploadDocuments";
import { useDocumentDrop } from "@src/components/modules/Upload/useDocumentDrop";
import { DocumentPreviewPanel } from "@src/components/modules/panels/DocumentPreviewPanel";
import React, {
  DragEvent,
  ForwardedRef,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { v4 } from "uuid";
import {
  DocumentManagerProps,
  DocumentForDocumentManager,
  StyledRoot,
} from "./DocumentManager";

export type FileChangeTrigger = {
  clear: () => void;
};

type DocumentManagerRootProps = Pick<
  DocumentManagerProps,
  "error" | "className" | "bare" | "onClose"
> & {
  loading: boolean;
  attachedDocs: DocumentForDocumentManager[];
  children?: React.ReactNode;
  activeDocument?: {
    displayName: string;
    id: string;
  };
  onDocsAdded: (docs: File[]) => void;
  onRemoveDoc: (doc: DocumentForDocumentManager) => Promise<void>;
  onDocumentPreviewClose: () => void;
  onDocumentSelected: (doc: DocumentForDocumentManager) => void;
  disablePreview?: boolean;
  documentManagerRef?: React.Ref<HTMLDivElement>;
  ref?: ForwardedRef<FileChangeTrigger>;
  showCloseButton?: boolean;
};
export const DocumentManagerRoot = forwardRef<
  FileChangeTrigger,
  DocumentManagerRootProps
>(
  (
    {
      onDocsAdded,
      className,
      error,
      loading,
      bare,
      onClose,
      attachedDocs,
      onRemoveDoc: removeDoc,
      children,
      onDocumentPreviewClose,
      onDocumentSelected,
      activeDocument,
      disablePreview,
      documentManagerRef,
      showCloseButton = true,
    },
    ref: ForwardedRef<FileChangeTrigger>,
  ) => {
    const attachedDocsEl = useRef<HTMLDivElement>(null);
    const id = useRef(`document-manager-${v4()}`);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      clear() {
        inputRef.current ? (inputRef.current.value = "") : void 0;
      },
    }));

    const { bond, state, selectFile } = useDocumentDrop({
      onDocsAdded,
      allowedTypes: allowedDocTypes,
    });

    const stableDocs = JSON.stringify(attachedDocs);

    useEffect(() => {
      const attachedDocsDiv = attachedDocsEl.current;
      if (attachedDocsDiv) {
        attachedDocsDiv.scrollTop = attachedDocsDiv.scrollHeight;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stableDocs]);

    return (
      <StyledRoot
        ref={documentManagerRef}
        error={!!error}
        className={className}
        onDragEnter={(event: DragEvent) => {
          bond.onDragEnter(event);
          event.preventDefault();
        }}
      >
        <div
          className={`drop-area${state.over ? " hover" : ""}`}
          onDragLeave={(event) => {
            bond.onDragLeave(event);
          }}
          onDragOver={(event) => {
            bond.onDragOver(event);
          }}
          onDrop={(event) => {
            bond.onDrop(event);
          }}
          onPaste={(event) => {
            bond.onPaste(event);
          }}
        ></div>
        {activeDocument && !disablePreview ? (
          <DocumentPreviewPanel
            className="document-preview"
            documentId={activeDocument.id}
            onClickClose={onDocumentPreviewClose}
          />
        ) : (
          <>
            {onClose && showCloseButton ? (
              <CloseIcon className="closeButton" onClick={onClose} />
            ) : null}
            {bare ? null : (
              <h3 className="row center margin-ends-med">Attach document(s)</h3>
            )}
            {!!attachedDocs.length && (
              <div className="attached-docs" ref={attachedDocsEl}>
                <h4>Linked files</h4>
                {attachedDocs.map((doc) => (
                  <Box
                    key={doc.id}
                    className="attached box margin-top row font-small"
                    size="small"
                  >
                    <>
                      <ViewIcon
                        className="view-icon"
                        onClick={() => onDocumentSelected(doc)}
                      />
                      <div
                        className="label"
                        onClick={() => onDocumentSelected(doc)}
                      >
                        {doc.displayName}
                      </div>
                      <CloseIcon
                        className="close"
                        onClick={() => removeDoc(doc)}
                      />
                    </>
                  </Box>
                ))}
              </div>
            )}
            <div className="column centered margin-top margin-bottom xl">
              <label htmlFor={id.current} className="file-input-label">
                Choose a file
              </label>
              <input
                id={id.current}
                type="file"
                style={{ display: "none" }}
                onChange={selectFile}
                value={activeDocument?.id}
                ref={inputRef}
              />
              <span>or drag and drop anywhere</span>
              {loading && <Spinner className="spinner" />}
            </div>
            {children}
            <div
              className="error"
              style={{ fontSize: "var(--font-size-small)" }}
            >
              {error ?? <>&nbsp;</>}
            </div>
          </>
        )}
      </StyledRoot>
    );
  },
);
